import styles from './TravellerSelector.module.scss'
import SelectWidget from '@/components/elements/inputs/SelectWidget'
import { useTranslation } from 'next-i18next'
import travelPricingAgeGroupObject from '@/entities/travelPricingAgeGroup/interface'
import travelPricingAgeGroupSettingsObject from '@/entities/travelPricingAgeGroupSettings/interface'
import { Button, Col, Container, Row } from 'react-bootstrap'
import NumberToggle from '@/components/elements/inputs/NumberToggle'
import { useContext, useEffect, useState } from 'react'
import BirthdayPicker from '@/components/elements/pickers/BirthdayPicker'
import { toDbDate } from '@/lib/helpers/helpers'
import { TravelIdContext } from '@/app/context'
import { useAppDispatch, useAppSelector } from '@/app/hooks'
import { getStateBooking, selectBookings, setTravellers, setChildren as setBookingChildren, setBabies as setBookingBabies } from '@/features/booking'
import { bookingStateItem, bookingStateTraveller, child } from '@/features/booking/types'
import LocalBooking from '@/lib/booking/LocalBooking'

const dateIsInInRange = (proofDate: any, startDate: Date, endDate: Date) => {
  // @ts-ignore
  if (!(proofDate instanceof Date) || isNaN(proofDate)) {
    return false
  }

  if (proofDate.getTime() < startDate.getTime()) {
    return false
  }

  if (proofDate.getTime() > endDate.getTime()) {
    return false
  }

  return true
}

const getChildrenAgeRange = (ageGroups: travelPricingAgeGroupObject[]) => {
  let minAge = 1000
  let maxAge = 0
  ageGroups
    .filter((ageGroup) => {
      return ageGroup.type === 'MINOR'
    })
    .forEach((ageGroup) => {
      if (ageGroup.minAge < minAge) {
        minAge = ageGroup.minAge
      }
      if (ageGroup.maxAge > maxAge) {
        maxAge = ageGroup.maxAge
      }
    })
  return {
    min: minAge,
    max: maxAge,
  }
}

interface Props {
  ageGroupSettings: travelPricingAgeGroupSettingsObject
  colorTheme?: 'light' | 'dark'
}

const TravellerSelector = ({ ageGroupSettings, colorTheme = 'light' }: Props) => {
  const { t } = useTranslation('common')

  const travelId = useContext(TravelIdContext)
  const dispatch = useAppDispatch()
  const bookings = useAppSelector(selectBookings)
  const booking = getStateBooking(bookings, travelId) as bookingStateItem
  const manager = new LocalBooking(booking)
  const childrenAllowed = manager.childrenAllowed()
  const babiesAllowed = manager.babiesAllowed()

  const [open, setOpen] = useState<boolean | null>(null)
  const [inputValue, setInputValue] = useState<string>(manager.getTravellerSelectionLabel(t))
  const [children, setChildren] = useState<child[]>(booking.travellers.filter((traveller) => traveller.ageGroup.type === 'MINOR'))
  const [babies, setBabies] = useState<child[]>([...booking.babies])

  const adultAgeGroup = manager.getAdultAgeGroup(ageGroupSettings.ageGroups)

  const handleClose = () => {
    setOpen(open === false ? null : false)
  }

  useEffect(() => {
    setInputValue(manager.getTravellerSelectionLabel(t))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [booking.travellers, booking.babies])

  const handleChangeTraveller = (ageGroup: travelPricingAgeGroupObject, action: 'increase' | 'decrease') => {
    let travellers = [...booking.travellers]
    // add/remove
    switch (action) {
      case 'increase':
        travellers.push({
          ageGroup: ageGroup,
        } as bookingStateTraveller)
        break

      case 'decrease':
        const index = travellers.findIndex((traveller: bookingStateTraveller) => {
          return ageGroup.id === traveller.ageGroup.id
        })
        travellers.splice(index, 1)
        break
    }
    // travellers = orderTravellers(travellers, ageGroupSettings.ageGroups)
    dispatch(setTravellers({ travelId: booking.travelId, travellers: travellers }))
    setInputValue(manager.getTravellerSelectionLabel(t))
  }

  const childrenAgeRange = getChildrenAgeRange(ageGroupSettings.ageGroups)

  const handleChangeNumberOfChildren = (action: 'increase' | 'decrease') => {
    switch (action) {
      case 'increase':
        children.push({
          birthday: '',
        })
        break
      case 'decrease':
        children.splice(-1, 1)
        break
    }
    dispatchChildren(children)
    setChildren([...children])
  }

  const setChildBirthday = (index: number, birthday: Date | null) => {
    const newChildren = children.map((child) => {
      return { ...child }
    })
    newChildren[index].birthday = birthday ? toDbDate(birthday) : ''
    setChildren([...newChildren])
    if (!missingChildrenBirthdaysCount(newChildren)) {
      dispatchChildren(newChildren)
    }
  }

  const dispatchChildren = (children: child[]) => {
    dispatch(
      setBookingChildren({
        travelId: booking.travelId,
        children: children.map((child) => {
          return { birthday: child.birthday }
        }),
      })
    )
  }

  const missingChildrenBirthdaysCount = (children: child[]) => {
    let missingCount = 0
    children.forEach((child) => {
      if (!child.birthday) {
        missingCount++
      }
    })
    return missingCount
  }

  const getChildBirthdayMinDate = () => {
    // get minors max age from ageGroups
    let maxAge = 0
    ageGroupSettings.ageGroups
      .filter((ageGroup) => {
        return ageGroup.type === 'MINOR'
      })
      .forEach((ageGroup) => {
        if (maxAge < ageGroup.maxAge) {
          maxAge = ageGroup.maxAge
        }
      })

    // calculate minDate from travel end date + 1 day
    // @ts-ignore
    const date = new Date(booking.travelDate.endDate)
    date.setFullYear(date.getFullYear() - maxAge - 1)
    date.setDate(date.getDate() + 1)
    return date
  }

  const getChildBirthdayMaxDate = () => {
    // get minors max age from ageGroups
    let minAge = 999
    ageGroupSettings.ageGroups
      .filter((ageGroup) => {
        return ageGroup.type === 'MINOR'
      })
      .forEach((ageGroup) => {
        if (minAge > ageGroup.minAge) {
          minAge = ageGroup.minAge
        }
      })

    // calculate minDate from travel end date
    // @ts-ignore
    const date = new Date(booking.travelDate?.endDate)
    date.setFullYear(date.getFullYear() - minAge)
    date.setDate(date.getDate())
    return date
  }

  const dispatchBabies = (babies: child[]) => {
    if (!missingBabiesBirthdaysCount(babies)) {
      dispatch(
        setBookingBabies({
          travelId: booking.travelId,
          babies: babies.map((baby) => {
            return { birthday: baby.birthday }
          }),
        })
      )
    }
  }

  const handleChangeNumberOfBabies = (action: 'increase' | 'decrease') => {
    switch (action) {
      case 'increase':
        babies.push({
          birthday: '',
        })
        break
      case 'decrease':
        babies.splice(-1, 1)
        break
    }
    dispatchBabies(babies)
    setBabies([...babies])
  }

  const missingBabiesBirthdaysCount = (babies: child[]) => {
    let missingCount = 0
    babies.forEach((baby) => {
      if (!baby.birthday) {
        missingCount++
      }
    })
    return missingCount
  }

  const setBabyBirthday = (index: number, birthday: Date | null) => {
    const newBabies = babies.map((child) => {
      return { ...child }
    })
    newBabies[index].birthday = birthday ? toDbDate(birthday) : ''
    setBabies([...newBabies])
    dispatchBabies(newBabies)
  }

  const getBabyBirthdayMinDate = () => {
    // get minors max age from ageGroups
    let maxAge = Number(booking.travel?.pricing.ageGroupSettings.freeUntilAge)

    // calculate minDate from travel start date
    // @ts-ignore
    const date = new Date(booking.travelDate?.endDate)
    date.setFullYear(date.getFullYear() - maxAge)
    date.setDate(date.getDate() + 1)
    return date
  }

  return (
    <>
      <SelectWidget
        variant={colorTheme === 'light' ? 'default' : 'whiteOutline'}
        open={open}
        placeholder={t('booking.numberOfPeople')}
        inputValue={inputValue}
        preventCloseOnClickOutside={childrenAllowed || babiesAllowed}
        disabled={!booking.travelDate}
        renderWidget={() => {
          return (
            <>
              <div className={styles.widget}>
                <Container fluid className="px-2">
                  <Row className={`justify-content-between align-items-center ${styles.ageGroup}`}>
                    <Col xs={'auto'}>
                      <div>
                        <span className={styles.label}>{childrenAllowed || babiesAllowed ? t('booking.adults') : t('booking.persons')}</span>
                      </div>
                    </Col>
                    <Col xs={'auto'}>
                      <NumberToggle
                        value={booking.travellers.filter((traveller) => traveller.ageGroup.type === 'ADULT').length}
                        onChange={(value: number, action: 'increase' | 'decrease') => {
                          handleChangeTraveller(adultAgeGroup, action)
                        }}
                      />
                    </Col>
                  </Row>
                  {childrenAllowed && (
                    <>
                      <Row className={`justify-content-between align-items-center ${styles.ageGroup}`}>
                        <Col xs={'auto'}>
                          <div>
                            <span className={styles.label}>
                              {t('booking.children')}{' '}
                              <span className={styles.ageRange}>
                                ({t('booking.ageRange', { minAge: childrenAgeRange.min, maxAge: childrenAgeRange.max })})
                              </span>
                            </span>
                          </div>
                        </Col>
                        <Col xs={'auto'}>
                          <NumberToggle
                            value={children.length}
                            onChange={(value: number, action: 'increase' | 'decrease') => {
                              handleChangeNumberOfChildren(action)
                            }}
                          />
                        </Col>
                        {[...Array(children.length)].map((element, index: number) => {
                          const maxBirthday = getChildBirthdayMaxDate()
                          const minBirthday = getChildBirthdayMinDate()
                          return (
                            <div key={index} className="d-flex align-items-center pt-1">
                              <Col>
                                {t('booking.child')} {index + 1}
                              </Col>
                              <Col xs={'auto'}>
                                <BirthdayPicker
                                  width={'140px'}
                                  value={children[index].birthday ? new Date(children[index].birthday) : null}
                                  onChange={(newValue) => {
                                    if (dateIsInInRange(newValue, minBirthday, maxBirthday)) {
                                      setChildBirthday(index, newValue)
                                    }
                                  }}
                                  maxDate={maxBirthday}
                                  minDate={minBirthday}
                                />
                              </Col>
                            </div>
                          )
                        })}
                        {!!missingChildrenBirthdaysCount(children) && (
                          <div className={styles.childError}>
                            {t('booking.childrenBirthdayMissing', {
                              count: missingChildrenBirthdaysCount(children),
                            })}
                          </div>
                        )}
                      </Row>
                    </>
                  )}
                  {babiesAllowed && (
                    <>
                      <Row className={`justify-content-between align-items-center ${styles.ageGroup}`}>
                        <Col xs={'auto'}>
                          <div>
                            <span className={styles.label}>
                              {t('booking.babies')}{' '}
                              <span className={styles.ageRange}>
                                ({t('booking.untilAge', { age: booking.travel?.pricing.ageGroupSettings.freeUntilAge })})
                              </span>
                            </span>
                          </div>
                        </Col>
                        <Col xs={'auto'}>
                          <NumberToggle
                            value={babies.length}
                            onChange={(value: number, action: 'increase' | 'decrease') => {
                              handleChangeNumberOfBabies(action)
                            }}
                          />
                        </Col>
                        {[...Array(babies.length)].map((element, index: number) => {
                          const minBirthday = getBabyBirthdayMinDate()
                          const maxBirthday = new Date()
                          return (
                            <div key={index} className="d-flex align-items-center pt-1">
                              <Col>
                                {t('booking.baby')} {index + 1}
                              </Col>
                              <Col xs={'auto'}>
                                <BirthdayPicker
                                  width={'140px'}
                                  value={babies[index].birthday ? new Date(babies[index].birthday) : null}
                                  onChange={(newValue) => {
                                    if (dateIsInInRange(newValue, minBirthday, maxBirthday)) {
                                      setBabyBirthday(index, newValue)
                                    }
                                  }}
                                  minDate={minBirthday}
                                />
                              </Col>
                            </div>
                          )
                        })}
                        {!!missingBabiesBirthdaysCount(babies) && (
                          <div className={styles.childError}>
                            {t('booking.childrenBirthdayMissing', {
                              count: missingBabiesBirthdaysCount(babies),
                            })}
                          </div>
                        )}
                      </Row>
                    </>
                  )}
                  <Row>
                    <Col className="text-end pt-2">
                      <Button
                        variant="outline-dark"
                        size="sm"
                        onClick={handleClose}
                        disabled={!!missingChildrenBirthdaysCount(children) || !!missingBabiesBirthdaysCount(babies)}
                      >
                        {t('booking.next')}
                      </Button>
                    </Col>
                  </Row>
                </Container>
              </div>
            </>
          )
        }}
      />
    </>
  )
}

export default TravellerSelector
